import type { FieldValues, Path } from 'react-hook-form';

import { SDSCheckbox } from '@repo/sds/SDSCheckbox';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';

import type { ControlledFieldProps } from '../FormField/FormField';

/** Base configuration props specific to the Checkbox Input component. */
export interface BaseFormCheckboxInputProps<TFieldValues extends FieldValues> {
  /** Name identifier for the field within the form state. */
  name: Path<TFieldValues>;
  /** Text label displayed next to the checkbox. */
  label: string;
  /** Helper text displayed below the checkbox. */
  helperText?: string;
  /** Data attribute for testing purposes. */
  dataTestId?: string;
}

/** Combined props for the FormCheckboxInput component, including RHF control props. */
interface FormCheckboxInputProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends BaseFormCheckboxInputProps<TFieldValues>,
    ControlledFieldProps<TFieldValues, TName> {}

/**
 * Renders an SDSCheckbox component integrated with React Hook Form,
 * receiving RHF state and handlers via the 'field' prop from a Controller wrapper.
 */
export function FormCheckboxInput<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({ field, errorMessage, isDisabled, label, helperText, dataTestId }: FormCheckboxInputProps<TFieldValues, TName>) {
  const isChecked = Boolean(field.value);

  return (
    <SDSFlexbox direction="column" gap="no-design-4" fullWidth>
      <SDSFlexbox gap="no-design-8" alignItems="center">
        <SDSCheckbox
          isChecked={isChecked}
          onChange={field.onChange}
          isDisabled={isDisabled}
          isError={Boolean(errorMessage)}
          name={field.name}
          dataTestId={dataTestId}
          ariaLabel={label}
        />
        <SDSText text={label} type="body-medium" color={isDisabled ? 'muted' : undefined} />
      </SDSFlexbox>
      {helperText && <SDSText text={helperText} type="body-small" color="muted" />}
      {errorMessage && <SDSText text={errorMessage} type="body-small" color="alert" />}
    </SDSFlexbox>
  );
}
